import {
  FEDEX_SHIPIUM_ACCOUNT_NUMBER,
  FEDEX_SHIPIUM_API_KEY,
  FEDEX_SHIPIUM_API_PASSWORD,
  FEDEX_SHIPIUM_METER_NUMBER,
} from '../../../services/FP_FulfillmentContextService/types';
import type { AssetState, AssetWorkflowOptions } from '../../types';

export const getSupportedCarriersForAddressType = (
  assetState: AssetState,
  addressType: 'return_to' | 'sort_facility' | 'shipper',
): string[] => {
  // enums cannot be empty lists
  // this should not happen in the wild
  // but it breaks dev mode and it doesn't
  // hurt to be safe
  const carrierIds = Object.values(assetState.carrier)
    .filter((c) => c.carrierAccountAddressTypes?.includes(addressType))
    .map((c) => c.carrierId);
  return carrierIds.length > 0 ? carrierIds : ['surely_carrier_with_this_id_would_never_exist'];
};
export const addCarrierAccountWorkflow: AssetWorkflowOptions = {
  workflowKey: 'addCarrierAccountWorkflow',
  workflowIndex: 1,
  assetType: 'carrierAccount',
  workflowType: 'addAssetWorkflow',
  workflowName: 'Add Carrier Account Workflow',
  workflowStateTransformFunction: (assetPayload: any) => {
    const { fedexSmartPostValue, carrierId, description, name, credentials, costCenter, partnerProvidedId } =
      assetPayload;
    return {
      partnerProvidedId,
      name,
      carrierId,
      description,
      credentials,
      costCenter,
      ...(carrierId === 'fedex'
        ? {
            shipiumCredentials: {
              credentialStructureId: 'fedex-shipium-credentials',
              credentials: {
                [FEDEX_SHIPIUM_API_KEY]: 'TEST_PLACEHOLDER',
                [FEDEX_SHIPIUM_API_PASSWORD]: 'TEST_PLACEHOLDER',
                [FEDEX_SHIPIUM_ACCOUNT_NUMBER]: 'TEST_PLACEHOLDER',
                [FEDEX_SHIPIUM_METER_NUMBER]: 'TEST_PLACEHOLDER',
              },
            },
          }
        : {}),
      ...(fedexSmartPostValue
        ? {
            serviceMethodCredentials: [
              // smart post has been split into 2 methods so now we add credentials for both
              {
                credentialStructureId: 'fedex-standard-credentials',
                carrierServiceMethodId: 'fedex-smartpost-service-method',
                credentialId: 'fedex-smartpost-hub-id',
                credentialValue: fedexSmartPostValue,
              },
              {
                credentialStructureId: 'fedex-standard-credentials',
                carrierServiceMethodId: 'fedex-smartpost-lightweight-service-method',
                credentialId: 'fedex-smartpost-hub-id',
                credentialValue: fedexSmartPostValue,
              },
            ],
          }
        : {}),
    };
  },
  workflowDescription: `
Each time you want to use a new carrier account, you will add that account to Shipium so that it is accessible. Broadly speaking, you will set up information about the carrier such as account number and other organization-specific billing information as well as some details about which FCs will utilize the carrier account.

Once an account has been configured, you can then add service methods to it (e.g. Ground, Next Day Air, etc.) and set configuration values associated with the service method.
  `,
  workflowStepOptions: [
    {
      workflowStepKey: 'carrierSelect',
      workflowStepNavigationName: 'Carrier',
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
        suffix: ' Account',
      },
      workflowComponent: {
        singleSelect: true,
        hideServiceMethods: true,
        workflowComponentType: 'carrierServiceMethodEntry',
        prompt: 'Which carrier does this account apply to?',
        description: 'We need to know which carrier you are using to guide you through setting up your account',
        help: `If you don't see your carrier here please contact a shipium admin `,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'carrierId',
      },
    },
    // shipper
    {
      workflowStepKey: 'shipperAddress',
      workflowStepNavigationName: 'Shipper Address',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          generateSchema: (assetState: AssetState) => ({
            type: 'string',
            enum: getSupportedCarriersForAddressType(assetState, 'shipper'),
          }),
        },
      ],
      workflowComponent: {
        workflowComponentType: 'addressEntry',
        prompt: 'What is the ship from address for this carrier account?',
        description: 'Some carrier integrations require a ship from addresses.',
        help: 'Enter your address to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'address',
        linkAssetTypeOverride: 'shipperAddresses',
      },
    },
    {
      workflowStepKey: 'fulfillmentCenterContact',
      workflowStepNavigationName: 'Shipper Contact',
      excludeFromNavigation: false,
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          generateSchema: (assetState: AssetState) => ({
            type: 'string',
            enum: getSupportedCarriersForAddressType(assetState, 'shipper'),
          }),
        },
      ],
      workflowComponent: {
        workflowComponentType: 'contactEntry',
        prompt: 'Who is the ship from contact for this carrier account?',
        description: 'Some carrier integrations require a ship from contact',
        help: 'Enter your contact to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'contact',
        linkAssetTypeOverride: 'shipperContacts',
      },
    },
    // sort facility
    {
      workflowStepKey: 'sortFacilityAddress',
      workflowStepNavigationName: 'Sort Facility Address',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          generateSchema: (assetState: AssetState) => ({
            type: 'string',
            enum: getSupportedCarriersForAddressType(assetState, 'sort_facility'),
          }),
        },
      ],
      workflowComponent: {
        workflowComponentType: 'addressEntry',
        prompt: 'What is the sort facility address for this carrier account?',
        description: 'Some carrier integrations require a sort facility address.',
        help: 'Enter your address to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'address',
        linkAssetTypeOverride: 'sortFacilityAddress',
      },
    },
    {
      workflowStepKey: 'sortFacilityContact',
      workflowStepNavigationName: 'Sort Facility Contact',
      excludeFromNavigation: false,
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          generateSchema: (assetState: AssetState) => ({
            type: 'string',
            enum: getSupportedCarriersForAddressType(assetState, 'sort_facility'),
          }),
        },
      ],
      workflowComponent: {
        workflowComponentType: 'contactEntry',
        prompt: 'Who is the sort facility contact for this carrier account?',
        description: 'Some carrier integrations require a sort facility contact',
        help: 'Enter your contact to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'contact',
        linkAssetTypeOverride: 'sortFacilityContact',
      },
    },
    // Return To
    {
      workflowStepKey: 'returnToAddress',
      workflowStepNavigationName: 'Return To Address',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      optional: true,
      workflowComponent: {
        workflowComponentType: 'addressEntry',
        prompt: 'What is the Return to address for this carrier account?',
        description: 'Some carrier integrations require a return to address.',
        help: 'Enter your address to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'address',
        linkAssetTypeOverride: 'returnToAddress',
      },
    },
    {
      workflowStepKey: 'returnToContact',
      workflowStepNavigationName: 'Return To Contact',
      excludeFromNavigation: false,
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          generateSchema: (assetState: AssetState) => ({
            type: 'string',
            enum: getSupportedCarriersForAddressType(assetState, 'return_to'),
          }),
        },
      ],
      workflowComponent: {
        workflowComponentType: 'contactEntry',
        prompt: 'Who is the Return to contact for this carrier account?',
        description: 'Some carrier integrations require a return to contact',
        help: 'Enter your contact to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'contact',
        linkAssetTypeOverride: 'returnToContact',
      },
    },
    // USPPI
    {
      workflowStepKey: 'usppiAddress',
      workflowStepNavigationName: 'USPPI Address',
      excludeFromNavigation: false,
      refreshAssetsOnStepCompletion: true,
      optional: true,
      workflowComponent: {
        workflowComponentType: 'addressEntry',
        prompt: 'What is the USPPI address for this carrier account?',
        description: 'Carriers may require a USPPI address to ship internationally',
        help: 'Enter the address to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'address',
        linkAssetTypeOverride: 'usppiAddress',
      },
    },
    {
      workflowStepKey: 'usppiContact',
      workflowStepNavigationName: 'USPPI Contact',
      excludeFromNavigation: false,
      optional: true,
      workflowComponent: {
        workflowComponentType: 'contactEntry',
        prompt: 'Who is the USPPI contact for this carrier account?',
        description: 'Carriers may require a USPPI contact to ship internationally',
        help: 'Enter the contact to continue',
        options: [],
      },
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'contact',
        linkAssetTypeOverride: 'usppiContact',
      },
    },
    {
      workflowStepKey: 'carrierAccountCredentials',
      workflowStepNavigationName: 'Credentials',
      workflowComponent: {
        workflowComponentType: 'credentialEntry',
        prompt: 'Enter your credentials for this account',
        description: 'Shipium needs access to these credentials to perform calls to the carrier on your behalf',
        help: '',
      },
      calculatedComponentProps: {
        carrierId: {
          property: 'carrierSelect',
          type: 'workflowStep',
        },
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'credentials',
      },
      // This step isn't optional but I don't want the user to able to naivagte here
      // here before selecting a workflow carrier
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          schema: { type: 'string' },
        },
      ],
    },
    {
      workflowStepKey: 'fedexSmartPost',
      workflowStepNavigationName: 'Fedex Smart Post',
      workflowComponent: {
        workflowComponentType: 'singleSelect',
        prompt: 'Do you use fedex smart post?',
        description: 'We will need to collect additional information if you use smartpost',
        help: '',
        options: [
          { optionText: 'Yes', optionValue: true },
          { optionText: 'No', optionValue: false },
        ],
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
      // This step isn't optional but I don't want the user to able to naivagte here
      // here before selecting a workflow carrier
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'carrierSelect',
          schema: { type: 'string', const: 'fedex' },
        },
      ],
    },
    {
      workflowStepKey: 'fedexSmartPostValue',
      workflowStepNavigationName: 'Fedex Smart Post Credentials',
      workflowComponent: {
        workflowComponentType: 'fedexSmartPostHubSelect',
        description: '',
        prompt: 'Enter your Fedex SmartPost Hub ID',
        help: 'Fedex SmartPost requires your hub id to ship',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'fedexSmartPostValue',
      },
      // This step isn't optional but I don't want the user to able to naivagte here
      // here before selecting a workflow carrier
      workflowStepConditionalMatcher: [
        {
          workflowStepKey: 'fedexSmartPost',
          schema: { type: 'boolean', const: true },
        },
        {
          workflowStepKey: 'carrierSelect',
          schema: { type: 'string', const: 'fedex' },
        },
      ],
    },
    {
      workflowStepKey: 'costCenter',
      workflowStepNavigationName: 'Cost Center',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Do you want to provide a cost center identifier for this account?',
        description: `A cost center identifier can be used for categorizing expenses. When a carrier's API supports it, this field will be sent to the carrier to facilitate accounting.`,
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'costCenter',
      },
    },
    {
      workflowStepKey: 'partnerProvidedId',
      workflowStepNavigationName: 'Carrier Account ID Alias',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        prompt: 'Enter a unique identifier for this carrier account',
        description: 'This is optional but will allow you to more easily reference this carrier account in API calls.',
        help: '',
      },
      includeInDefaultAssetNameRules: {
        includeInDefaultAssetName: true,
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'partnerProvidedId',
      },
    },
    {
      workflowStepKey: 'name',
      workflowStepNavigationName: 'Carrier Account Name',
      workflowComponent: {
        workflowComponentType: 'assetNameEntry',
        prompt: 'How do you want to refer to this account?',
        description:
          'Tell us what you want to call this acccount. Using something the involves that Carrier name is generally a good choice',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'name',
      },
    },
    {
      workflowStepKey: 'carrierAccountDescription',
      workflowStepNavigationName: 'Carrier Account Description',
      optional: true,
      workflowComponent: {
        workflowComponentType: 'textEntry',
        rows: 4,
        prompt: 'Enter a description (optional):',
        description: '',
        defaultValue: '',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'property',
        property: 'description',
      },
    },
    {
      workflowStepKey: 'tenantSelect',
      workflowStepNavigationName: 'Tenant Select',
      workflowComponent: {
        assetType: 'tenant',
        workflowComponentType: 'tenantSelect',
        prompt: 'Does this carrier account belong to a specific tenant?',
        description: '',
        help: '',
        optional: true,
      },
      optional: true,
      workflowStepConditionalMatcher: [
        {
          assetType: 'tenant',
          schema: { type: 'array', minItems: 1 },
        },
      ],
      workflowStateToAssetMapping: {
        mappingType: 'link',
        linkAssetType: 'tenant',
        linkType: 'Tenant',
      },
    },
    {
      workflowStepKey: 'carrierAccountSummary',
      workflowStepNavigationName: 'Carrier Account Summary',
      workflowComponent: {
        workflowComponentType: 'workflowSummary',
        title: 'Carrier Account Summary',
        description: 'The following values will be saved.  Please review your selections.',
        help: '',
      },
      workflowStateToAssetMapping: {
        mappingType: 'ignore',
      },
    },
  ],
};
